import React, { useEffect, useMemo, useState } from "react";
import { FileText, Upload, CheckCircle2, Clock, ArrowRight, Info, ShieldAlert, Download, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// GST Filing — mocked UI. Real GSTN integration needs GST portal credentials
// (GSTIN + username + one-time API token) which the user hasn't shared yet.
// This screen pulls sales/purchases from the P2P bill ledger, computes a
// GSTR-1 / GSTR-3B preview, and offers a "File now" button that is greyed
// out with a friendly note explaining the missing credential.
// -----------------------------------------------------------------------------

const RETURNS = [
  { code: "GSTR-1",  name: "Outward supplies",           freq: "Monthly / Quarterly", due: "11th of next month" },
  { code: "GSTR-3B", name: "Summary return + tax pay",   freq: "Monthly",              due: "20th of next month" },
  { code: "GSTR-9",  name: "Annual return",              freq: "Annual",               due: "31 Dec of next FY" },
  { code: "CMP-08",  name: "Composition dealer summary", freq: "Quarterly",            due: "18th of month following quarter" },
];

// Rupees helper — bill_paise → ₹
const fmt = (paise) => `₹${(Number(paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function GstFiling() {
  const { user } = useAuth();
  const [gstin, setGstin] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [period, setPeriod] = useState("current");         // current | last
  const [returnCode, setReturnCode] = useState("GSTR-3B");
  const [issued, setIssued] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useSeo({ title: "GST filing preview · Auto-drafted from your ledger · find2near",
           description: "Auto-draft GSTR-1 and GSTR-3B from your P2P bills. Preview outward/inward supplies and net GST payable before filing on the portal.",
           canonical: "/gst-filing" });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/erp/bills/issued").then(r => r.data).catch(() => []),
      api.get("/erp/bills/received").then(r => r.data).catch(() => []),
    ]).then(([iss, rec]) => { setIssued(iss); setReceived(rec); })
      .finally(() => setLoading(false));
  }, []);

  const summary = useMemo(() => {
    const now = new Date();
    const inPeriod = (iso) => {
      if (!iso) return false;
      const d = new Date(iso);
      if (period === "current") return d.getUTCMonth() === now.getUTCMonth() && d.getUTCFullYear() === now.getUTCFullYear();
      const last = new Date(now.getUTCFullYear(), now.getUTCMonth() - 1);
      return d.getUTCMonth() === last.getUTCMonth() && d.getUTCFullYear() === last.getUTCFullYear();
    };
    const filterList = (arr) => arr.filter(b => inPeriod(b.created_at));
    const outward = filterList(issued);
    const inward  = filterList(received);
    const sumTax  = (arr) => arr.reduce((s, b) => s + (b.tax_paise || 0), 0);
    const sumTot  = (arr) => arr.reduce((s, b) => s + (b.total_paise || 0), 0);
    const outputTax = sumTax(outward);
    const inputTax  = sumTax(inward);
    return {
      outward_count: outward.length,
      outward_taxable: sumTot(outward) - outputTax,
      outward_tax: outputTax,
      inward_count: inward.length,
      inward_taxable: sumTot(inward) - inputTax,
      inward_tax: inputTax,
      net_tax_payable: Math.max(0, outputTax - inputTax),
      itc_available: Math.min(outputTax, inputTax),
    };
  }, [issued, received, period]);

  const canFile = false;                                   // BLOCKED — no GSTN keys

  const fileReturn = async () => {
    setBusy(true);
    // No real API call — this button will be enabled once GSTN creds are added.
    setTimeout(() => {
      setBusy(false);
      toast.error("Filing is disabled until GSTN credentials are configured.");
    }, 500);
  };

  const downloadJson = () => {
    const draft = {
      return_code: returnCode, period, gstin: gstin || "not_set",
      trade_name: tradeName || user?.name || "", generated_at: new Date().toISOString(),
      summary, outward: issued, inward: received,
    };
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${returnCode}-${period}-draft.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(a.href);
    toast.success(`Draft ${returnCode} downloaded`);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="gst-page">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Building2 size={12} className="text-indigo-600"/> ERP · GST filing</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">File GST. <span className="text-indigo-700">From your ledger.</span></h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">We auto-draft your outward and inward supplies from every P2P bill. Preview GSTR-1, GSTR-3B or CMP-08 before you file on the government portal.</p>
      </div>

      <div className="rounded-3xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 mb-5" data-testid="gst-mock-banner">
        <ShieldAlert size={14} className="text-amber-700 mt-0.5 shrink-0"/>
        <div className="text-xs text-amber-900">
          <b>Preview mode.</b> This screen drafts your return locally from ledger data. To actually
          file, we need your <b>GSTIN</b>, <b>portal username</b> and a <b>one-time GSP API token</b>
          (Cygnet / TaxPro / ClearTax). Once shared, the <i>File now</i> button connects to the
          government portal via a licensed GSP.
        </div>
      </div>

      {/* GSTIN + return selector */}
      <div className="rounded-3xl bg-white border border-stone-200 p-4 grid md:grid-cols-4 gap-3 mb-4" data-testid="gst-config">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">GSTIN</div>
          <Input value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())} placeholder="22AAAAA0000A1Z5" data-testid="gst-gstin" className="rounded-full text-sm"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Legal / trade name</div>
          <Input value={tradeName} onChange={e => setTradeName(e.target.value)} placeholder={user?.name || "Your business"} data-testid="gst-trade" className="rounded-full text-sm"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Return</div>
          <Select value={returnCode} onValueChange={setReturnCode}>
            <SelectTrigger className="rounded-full text-sm" data-testid="gst-return"><SelectValue/></SelectTrigger>
            <SelectContent>{RETURNS.map(r => <SelectItem key={r.code} value={r.code}>{r.code} · {r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Period</div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="rounded-full text-sm" data-testid="gst-period"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current month</SelectItem>
              <SelectItem value="last">Last month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-stone-400"/></div>
      ) : (
        <>
          {/* Summary panels */}
          <div className="grid md:grid-cols-3 gap-3 mb-4" data-testid="gst-summary">
            <div className="rounded-3xl bg-gradient-to-br from-emerald-600 to-teal-700 text-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Outward supplies</div>
              <div className="text-2xl font-black">{summary.outward_count}</div>
              <div className="text-sm mt-1">Taxable: <b>{fmt(summary.outward_taxable)}</b></div>
              <div className="text-sm">Output GST: <b>{fmt(summary.outward_tax)}</b></div>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Inward supplies</div>
              <div className="text-2xl font-black">{summary.inward_count}</div>
              <div className="text-sm mt-1">Taxable: <b>{fmt(summary.inward_taxable)}</b></div>
              <div className="text-sm">Input GST: <b>{fmt(summary.inward_tax)}</b></div>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-amber-500 to-orange-700 text-white p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Net position</div>
              <div className="text-2xl font-black">{fmt(summary.net_tax_payable)}</div>
              <div className="text-sm mt-1">payable this cycle</div>
              <div className="text-sm">ITC used: <b>{fmt(summary.itc_available)}</b></div>
            </div>
          </div>

          {/* Actions */}
          <div className="rounded-3xl bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-2 mb-4" data-testid="gst-actions">
            <Button
              onClick={fileReturn}
              disabled={!canFile || busy}
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 font-black h-10 px-4"
              data-testid="gst-file-btn"
              title={canFile ? "" : "Add GSTN credentials to enable filing"}
            >
              {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Upload size={14} className="mr-1"/>}
              File {returnCode} now
            </Button>
            <Button
              onClick={downloadJson}
              variant="outline"
              className="rounded-full h-10 border-stone-300"
              data-testid="gst-download-btn"
            >
              <Download size={14} className="mr-1"/> Download draft ({returnCode})
            </Button>
            <div className="text-xs text-stone-500 ml-auto flex items-center gap-1">
              <Clock size={12}/> Due: {RETURNS.find(r => r.code === returnCode)?.due}
            </div>
          </div>

          {/* Return catalog */}
          <div className="rounded-3xl bg-white border border-stone-200 overflow-hidden mb-6" data-testid="gst-catalog">
            <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-widest font-black text-stone-600">All returns we can file for you</div>
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                <tr><th className="px-3 py-2 text-left">Code</th><th className="text-left">Name</th><th className="text-left">Frequency</th><th className="text-left">Due</th><th className="text-right pr-3">Draft</th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {RETURNS.map(r => (
                  <tr key={r.code} className="hover:bg-stone-50">
                    <td className="px-3 py-2 font-bold text-indigo-700">{r.code}</td>
                    <td className="text-stone-700">{r.name}</td>
                    <td className="text-stone-500">{r.freq}</td>
                    <td className="text-stone-500">{r.due}</td>
                    <td className="text-right pr-3"><button className="text-xs font-bold text-indigo-700 hover:underline" onClick={() => { setReturnCode(r.code); downloadJson(); }}>Draft <ArrowRight size={11} className="inline"/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* How it works */}
          <div className="rounded-3xl bg-stone-50 border border-stone-200 p-4 flex items-start gap-3" data-testid="gst-how">
            <Info size={16} className="text-stone-500 mt-0.5"/>
            <div className="text-xs text-stone-700 leading-relaxed space-y-1">
              <div><b>1.</b> Issue and receive bills in <a href="/erp" className="text-indigo-700 font-bold hover:underline">ERP → P2P Bills</a> — every line item carries the tax %.</div>
              <div><b>2.</b> We auto-classify each transaction as outward (sale) or inward (purchase), and split IGST / CGST / SGST by GSTIN state code.</div>
              <div><b>3.</b> Preview your GSTR-1 or GSTR-3B here. Click <b>Download draft</b> to keep an off-line copy for your CA.</div>
              <div><b>4.</b> Once GSTN credentials are added, <b>File now</b> pushes the return through a licensed GSP and returns the ARN acknowledgement.</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
